/**
 * An operator error travels from the engine to the screen as one message string:
 * the plain sentence first, then this marker, then the exact technical error.
 * Electron's IPC keeps only an Error's message, so the two halves share one
 * string and are split again where they are shown. The engine writes the same
 * marker (ContentCentralFailure.TechnicalDetailsMarker); keep them identical.
 */
export const TECHNICAL_DETAILS_MARKER = '\n\nTechnical details:\n';

export type OperatorError = { message: string; detail: string | null };

/** Splits a message into its plain sentence and its technical detail, if it carries one. */
export function splitTechnicalDetails(message: string | null | undefined): OperatorError {
  const text = message ?? '';
  const at = text.indexOf(TECHNICAL_DETAILS_MARKER);
  if (at < 0) return { message: text, detail: null };
  const detail = text.slice(at + TECHNICAL_DETAILS_MARKER.length).trim();
  return { message: text.slice(0, at).trim(), detail: detail || null };
}

/**
 * The desktop's own refusal of what it was asked to save, such as an export folder that is a watched
 * folder: a plain sentence for the operator, and what was refused (`refused`) for Technical details.
 * Thrown as it is, its message is the sentence alone (support's commands say that); `refusalShown`
 * marks it for a screen.
 */
export class SaveRefusal extends Error {
  readonly refused: string;
  constructor(sentence: string, refused: string) {
    super(sentence);
    this.name = 'SaveRefusal';
    this.refused = refused;
  }
}

/**
 * A refusal of what was asked to be saved, made by the engine or by the desktop itself. The engine's
 * own checks throw InvalidDataException with a plain sentence for the operator ("Choose a folder
 * outside it."); the desktop's throw `SaveRefusal`. That sentence becomes the one shown, with what
 * refused it behind Technical details, so a screen that puts its own "wasn't saved" in front of a real
 * failure shows the reason instead. Anything else (a real failure, a sentence that already carries
 * details, the verbose engine's stack) is returned unchanged.
 */
export function refusalShown(reason: unknown, command: string): unknown {
  if (!(reason instanceof Error)) return reason;
  if (reason instanceof SaveRefusal) {
    const said = reason.message.trim();
    if (!said || reason.message.includes(TECHNICAL_DETAILS_MARKER)) return reason;
    return new Error(
      `${said}${TECHNICAL_DETAILS_MARKER}Paige refused ${command}: ${reason.refused}.`,
    );
  }
  const kind = (reason as { engineErrorKind?: unknown }).engineErrorKind;
  const said = reason.message.trim();
  if (
    kind !== 'System.IO.InvalidDataException' ||
    !said ||
    reason.message.includes(TECHNICAL_DETAILS_MARKER) ||
    said.startsWith(kind)
  )
    return reason;
  return new Error(`${said}${TECHNICAL_DETAILS_MARKER}The engine refused ${command} (${kind}).`);
}

/** The plain sentence only, for places with no room for details (toasts). */
export function operatorMessage(message: string | null | undefined): string {
  return splitTechnicalDetails(message).message;
}
